/**
 * Captures recent runtime errors into a tiny ring buffer for feedback diagnostics.
 * Loaded BEFORE other scripts so the buffer is populated by the time the user opens
 * the feedback form. The buffer is capped at 20 entries and the serialized snapshot
 * is bounded to 4096 bytes; older entries are dropped first.
 *
 * Repeated identical errors are COALESCED (a `count` is incremented and the entry is
 * moved to most-recent) instead of appended. Without this, a noisy loop — e.g. a
 * repeatedly-failing guarded button — would flood all 20 slots with the same message
 * and evict the one distinct error that actually mattered before the user submits.
 */
let MoonfallErrorRingBuffer;

(function () {
  if (typeof window === 'undefined') return;
  // The double-install guard is gone with the global it read. A module is
  // evaluated once per graph, so there is nothing left to guard against — and
  // keeping `if (MoonfallErrorRingBuffer) return` would be a condition
  // that can now never be true, which reads as protection while providing none.

  var MAX_ENTRIES = 20;
  var MAX_BYTES = 4096;
  var MAX_MSG_LENGTH = 300;
  var MAX_SANITIZE_INPUT = 4000;
  var buf = [];

  function push(entry) {
    buf.push(entry);
    while (buf.length > MAX_ENTRIES) buf.shift();
  }

  function safeString(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
      if (value instanceof Error) return value.message || String(value);
      return String(value);
    } catch (_) {
      return '';
    }
  }

  function sanitizeText(value) {
    return safeString(value).slice(0, MAX_SANITIZE_INPUT)
      .replace(/\/(Users|home)\/[^/\s]+/g, '/$1/[redacted-user]')
      .replace(/\b[A-Z]:\\Users\\[^\\\s]+/gi, '[redacted-home]')
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
      .replace(/\beyJ[a-zA-Z0-9._-]{10,}\b/g, '[redacted-token]')
      .replace(/\b(?:sk|rk)_(?:test|live)_[A-Za-z0-9_]{4,}\b/g, '[redacted-secret]')
      .replace(/\bwhsec_[A-Za-z0-9_]{4,}\b/g, '[redacted-secret]')
      .replace(/\b(?:cus|acct|sub|in|pi|seti|pm|ch|re|dp)_[A-Za-z0-9_]{6,}\b/g, '[redacted-billing-id]')
      .replace(/\b(?:db|postgres(?:ql)?|redis|mongodb(?:\+srv)?):\/\/[^\s]+/gi, '[redacted-service-url]')
      .replace(/\b(?:Bearer|Basic)\s+[A-Za-z0-9._~+\/-]{12,}={0,2}\b/gi, '[redacted-token]')
      .replace(/\b(IP(?: address)?\s*[:=]?\s*)[A-F0-9:.]{3,}\b/gi, '$1[redacted-ip]')
      .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[redacted-ip]')
      .replace(/(["']?(?:firebaseuid|playerid|gameid|matchid|stableid|roomid|socketid|sessionid|connectionid|customerid|accountid|profileid|userid)["']?\s*[:=]\s*)["']?[A-Za-z0-9._-]{3,}["']?/gi, '$1[redacted-id]')
      .replace(/(["']?(?:access[_-]?token|auth(?:orization)?|cookie|credential|id[_-]?token|password|secret|token|api[_-]?key|session)["']?\s*[:=]\s*)["']?[^"'\s,;}]+["']?/gi, '$1[redacted]')
      .replace(/\b((?:(?:firebase|convex)\s+)?user\s+(?:signed\s+in|synced|authenticated)\s*[:=]\s*)["']?[^\s,;"']{3,}["']?/gi, '$1[redacted-id]')
      .replace(/\b((?:firebase(?:uid)?|player(?:id)?|game(?:id)?|match(?:id)?|stable(?:id)?|room(?:id)?|socket(?:id)?|session(?:id)?|connection(?:id)?|customer(?:id)?|account(?:id)?|profile(?:id)?|user(?:id)?)\s*(?:[:=]\s*|\s+(?!(?:signed|synced|authenticated)\b)))["']?[A-Za-z0-9._-]{6,}["']?/gi, '$1[redacted-id]')
      .replace(/([?&](?:access[_-]?token|auth[_-]?token|id[_-]?token|token|secret|api[_-]?key|session|code|state|signature|sig|policy|key-pair-id|x-(?:goog|amz)-[a-z0-9_-]+)=)[^&#\s]+/gi, '$1[redacted]')
      .replace(/\b((?:access[_-]?token|auth(?:orization)?|cookie|credential|id[_-]?token|password|secret|token|api[_-]?key|session)\s*[:=]\s*)[^\s,;]+/gi, '$1[redacted]');
  }

  function truncateMsg(value) {
    var s = sanitizeText(value);
    if (s.length > MAX_MSG_LENGTH) return s.slice(0, MAX_MSG_LENGTH);
    return s;
  }

  function signatureOf(entry) {
    return entry.kind + '\u0000' + entry.msg + '\u0000' + (entry.src || '') + '\u0000' +
      (entry.line == null ? '' : entry.line);
  }

  function record(entry) {
    if (!entry || typeof entry !== 'object') return;
    var clean = {
      t: typeof entry.t === 'number' ? entry.t : Date.now(),
      kind: typeof entry.kind === 'string' ? entry.kind : 'unknown',
      msg: truncateMsg(entry.msg),
    };
    if (entry.src) clean.src = truncateMsg(entry.src);
    if (entry.line != null) clean.line = Number(entry.line) || undefined;

    // Coalesce an identical error: bump its count, refresh last-seen time, and move it
    // to the most-recent slot so an actively-recurring error is protected from eviction.
    var sig = signatureOf(clean);
    for (var i = 0; i < buf.length; i++) {
      if (signatureOf(buf[i]) === sig) {
        var existing = buf.splice(i, 1)[0];
        existing.count = (existing.count || 1) + 1;
        existing.t = clean.t;
        buf.push(existing);
        return;
      }
    }
    push(clean);
  }

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('error', function (e) {
      record({ t: Date.now(), kind: 'error', msg: e && (e.message || e.error), src: e && e.filename, line: e && e.lineno });
    });
    window.addEventListener('unhandledrejection', function (e) {
      var reason = e && (e.reason && e.reason.message ? e.reason.message : e.reason);
      record({ t: Date.now(), kind: 'rejection', msg: reason });
    });
  }

  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    var origError = console.error.bind(console);
    console.error = function () {
      try {
        var parts = [];
        for (var i = 0; i < arguments.length; i++) parts.push(safeString(arguments[i]));
        record({ t: Date.now(), kind: 'console', msg: parts.join(' ') });
      } catch (_) {
        // never block the original console.error
      }
      return origError.apply(null, arguments);
    };
  }

  function snapshot() {
    var out = [];
    var bytes = 0;
    for (var i = buf.length - 1; i >= 0; i--) {
      var serialized = JSON.stringify(buf[i]);
      if (bytes + serialized.length > MAX_BYTES) break;
      bytes += serialized.length;
      out.unshift(buf[i]);
    }
    return out;
  }

  MoonfallErrorRingBuffer = {
    push: record,
    snapshot: snapshot,
    snapshotJson: function () { return JSON.stringify(snapshot()); },
    clear: function () { buf.length = 0; },
    _max: MAX_ENTRIES,
    _maxBytes: MAX_BYTES,
  };
})();

export { MoonfallErrorRingBuffer };
